import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Package, Clock, Phone, Mail, MapPin, ChevronRight, Globe, Gift, ShieldCheck, ShieldOff } from "lucide-react";
import { useTranslation } from "react-i18next";
import { apiClient, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { normalisePhoneInternational, isValidPhoneInternational } from "@/utils/phone";
import { formatGNF, STATUS_LABEL, STATUS_COLOR } from "@/utils/format";
import { toast } from "sonner";
import ProfilePictureUploader from "@/components/ProfilePictureUploader";
import { AddressBook, RecipientBook } from "@/components/AddressBook";

const LANG_OPTS = [
  { v: "fr", label: "🇫🇷 Français" },
  { v: "en", label: "🇬🇧 English" },
];

export default function Profile() {
  const { t } = useTranslation();
  const { user, logout, refresh } = useAuth();
  const [searchParams] = useSearchParams();
  // Bottom nav "Commandes" → scroll to the orders section
  useEffect(() => {
    if (searchParams.get("sec") === "orders") {
      setTimeout(() => document.getElementById("profile-orders")?.scrollIntoView({ behavior: "smooth", block: "start" }), 250);
    }
  }, [searchParams]);
  const [orders, setOrders] = useState([]);
  const [ordersTotal, setOrdersTotal] = useState(0);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [mfaPhone, setMfaPhone] = useState("");
  const [mfaCode, setMfaCode] = useState("");
  const [mfaStep, setMfaStep] = useState(0); // 0=idle, 1=code
  const [mfaBusy, setMfaBusy] = useState(false);
  const [deleteMode, setDeleteMode] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState("");
  const [deleteBusy, setDeleteBusy] = useState(false);

  const deleteAccount = async () => {
    setDeleteBusy(true);
    try {
      await apiClient.post("/auth/me/delete");
      toast.success(t("pr.deleted"));
      logout();
    } catch (err) {
      toast.error(formatApiError(err));
      setDeleteBusy(false);
    }
  };
  const PAGE = 20;

  const fetchPage = async (offset = 0, append = false) => {
    setOrdersLoading(true);
    try {
      const { data } = await apiClient.get("/orders/me", { params: { limit: PAGE, offset } });
      setOrdersTotal(data.total);
      setOrders((prev) => append ? [...prev, ...data.items] : data.items);
    } finally {
      setOrdersLoading(false);
    }
  };

  useEffect(() => {
    if (!user) return;
    fetchPage(0, false).catch(() => {});
  }, [user]);

  const setLang = async (lang) => {
    try {
      await apiClient.patch("/auth/me/lang", { lang });
      toast.success(t("pr.langUpdated"));
      refresh?.();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const saveAvatar = async (url) => {
    try {
      await apiClient.patch("/auth/me", { picture: url || "" });
      toast.success(url ? t("pr.photoUpdated") : t("pr.photoRemoved"));
      refresh?.();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  if (!user) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-20 text-center">
        <p>{t("pr.pleaseLogin")}</p>
        <Link to="/login" className="inline-block mt-4 bg-brand-red text-white px-5 py-3 rounded-full">{t("nav.login")}</Link>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <aside className="bg-white border border-sand rounded-2xl p-6">
          {user.picture ? (
            <img
              src={user.picture}
              alt={user.name}
              data-testid="profile-avatar-img"
              className="w-20 h-20 rounded-full object-cover border-2 border-sand"
            />
          ) : (
            <div className="w-16 h-16 rounded-full bg-brand-red text-white grid place-items-center font-display text-2xl font-semibold">
              {user.name?.[0]?.toUpperCase() || "U"}
            </div>
          )}
          <h2 className="font-display text-2xl font-semibold mt-3">{user.name}</h2>
          <p className="text-xs uppercase tracking-widest text-brand-red">{user.role}</p>
          <div className="mt-4 space-y-2 text-sm text-mute">
            <div className="flex items-center gap-2"><Phone className="w-4 h-4" /> {user.phone || "—"}</div>
            {user.email && <div className="flex items-center gap-2"><Mail className="w-4 h-4" /> {user.email}</div>}
          </div>

          <div className="mt-5">
            <ProfilePictureUploader
              value={user.picture || null}
              onChange={saveAvatar}
              hint={t("pr.photoHint")}
              testId="client-avatar"
              size={72}
            />
          </div>

          <div className="mt-5">
            <label className="text-xs uppercase tracking-widest text-mute mb-1 flex items-center gap-1"><Globe className="w-3 h-3" /> {t("profile.smsLang")}</label>
            <select
              data-testid="profile-lang-select"
              value={user.lang || "fr"}
              onChange={(e) => setLang(e.target.value)}
              className="w-full bg-elevated rounded-xl px-3 py-2 text-sm outline-none"
            >
              {LANG_OPTS.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
            </select>
          </div>

          {user.role === "client" && (
            <div className="mt-5 rounded-2xl p-4 bg-gradient-to-br from-[#009E60] to-[#0F7A4D] text-white" data-testid="loyalty-card">
              <div className="text-[10px] uppercase tracking-[0.25em] opacity-80 flex items-center gap-1.5">
                <Gift className="w-3 h-3" /> {t("profile.loyaltyTitle")}
              </div>
              <div className="font-display text-3xl font-bold mt-1">{user.loyalty_points || 0}<span className="text-base font-normal opacity-80"> pts</span></div>
              <p className="text-xs opacity-90 mt-1">{t("profile.loyaltyHelp")}</p>
            </div>
          )}

          {user.role === "client" && <AddressBook />}
          {user.role === "client" && <RecipientBook />}

          {/* MFA section */}
          <div className="mt-5 rounded-2xl p-4 bg-white border border-sand" data-testid="mfa-card">
            <div className="text-xs uppercase tracking-widest text-mute mb-2 flex items-center gap-1.5">
              {user.mfa_enabled ? <ShieldCheck className="w-3.5 h-3.5 text-brand-green" /> : <ShieldOff className="w-3.5 h-3.5" />}
              {t("profile.mfa")}
            </div>
            {user.mfa_enabled ? (
              <div className="space-y-2">
                <div className="text-sm">
                  <span className="text-brand-green font-semibold">{t("pr.mfaOn")}</span> — {t("pr.mfaOnHint")}
                </div>
                <button
                  data-testid="mfa-disable-btn"
                  disabled={mfaBusy}
                  onClick={async () => {
                    if (!window.confirm(t("pr.mfaDisableConfirm"))) return;
                    setMfaBusy(true);
                    try {
                      await apiClient.post("/auth/mfa/disable");
                      toast.success(t("pr.mfaDisabledToast"));
                      await refresh();
                    } catch (err) { toast.error(formatApiError(err)); }
                    finally { setMfaBusy(false); }
                  }}
                  className="w-full rounded-full border border-sand py-2 text-sm hover:bg-elevated"
                >
                  Désactiver la 2FA
                </button>
              </div>
            ) : mfaStep === 0 ? (
              <div className="space-y-2">
                <p className="text-xs text-mute">{t("pr.mfaOffHint")}</p>
                <input
                  data-testid="mfa-enroll-phone"
                  type="tel"
                  value={mfaPhone}
                  onChange={(e) => setMfaPhone(e.target.value)}
                  placeholder="+224 6XX XX XX XX"
                  className="w-full bg-elevated rounded-xl py-2 px-3 text-sm outline-none"
                />
                <button
                  data-testid="mfa-enroll-send"
                  disabled={mfaBusy || !mfaPhone}
                  onClick={async () => {
                    if (!isValidPhoneInternational(mfaPhone)) {
                      toast.error(t("pr.invalidNumber"));
                      return;
                    }
                    const normalised = "+" + normalisePhoneInternational(mfaPhone);
                    setMfaBusy(true);
                    try {
                      await apiClient.post("/auth/mfa/enroll/start", { phone: normalised });
                      setMfaPhone(normalised);
                      toast.success(t("pr.codeSent"));
                      setMfaStep(1);
                    } catch (err) { toast.error(formatApiError(err)); }
                    finally { setMfaBusy(false); }
                  }}
                  className="w-full rounded-full bg-brand-green text-white py-2 text-sm font-semibold disabled:opacity-60"
                >
                  Recevoir le code
                </button>
              </div>
            ) : (
              <div className="space-y-2">
                <p className="text-xs text-mute">{t("pr.codeReceivedAt")} <strong className="text-ink">{mfaPhone}</strong>.</p>
                <input
                  data-testid="mfa-enroll-code"
                  type="text" inputMode="numeric" maxLength={6}
                  value={mfaCode}
                  onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, ""))}
                  placeholder="123456"
                  className="w-full bg-elevated rounded-xl py-2 px-3 text-sm outline-none text-center font-display tracking-[0.3em]"
                />
                <button
                  data-testid="mfa-enroll-confirm"
                  disabled={mfaBusy || mfaCode.length < 4}
                  onClick={async () => {
                    setMfaBusy(true);
                    try {
                      await apiClient.post("/auth/mfa/enroll/confirm", { phone: mfaPhone, code: mfaCode });
                      toast.success(t("pr.mfaEnabledToast"));
                      setMfaStep(0); setMfaPhone(""); setMfaCode("");
                      await refresh();
                    } catch (err) { toast.error(formatApiError(err)); }
                    finally { setMfaBusy(false); }
                  }}
                  className="w-full rounded-full bg-brand-green text-white py-2 text-sm font-semibold disabled:opacity-60"
                >
                  Activer la 2FA
                </button>
                <button type="button" onClick={() => setMfaStep(0)} className="text-xs text-mute w-full">← Retour</button>
              </div>
            )}
          </div>

          <Link to="/change-password" data-testid="profile-change-password" className="mt-6 w-full rounded-full border border-sand py-2 text-sm hover:bg-elevated inline-flex items-center justify-center gap-2">{t("cp.title")}</Link>
          <button onClick={logout} data-testid="profile-logout" className="mt-2 w-full rounded-full border border-sand py-2 text-sm hover:bg-elevated">{t("profile.logout")}</button>

          {/* Zone dangereuse : suppression de compte (clients uniquement) */}
          {user?.role === "client" && (
            <div className="mt-5 rounded-2xl p-4 bg-white border border-brand-red/30" data-testid="delete-account-card">
              <div className="text-xs uppercase tracking-widest text-brand-red font-bold mb-2">{t("pr.dangerZone")}</div>
              {!deleteMode ? (
                <button onClick={() => setDeleteMode(true)} data-testid="delete-account-btn"
                  className="w-full rounded-full border border-brand-red/40 text-brand-red py-2 text-sm font-semibold hover:bg-brand-red hover:text-white transition-colors">
                  Supprimer mon compte
                </button>
              ) : (
                <div className="space-y-2">
                  <p className="text-xs text-mute">
                    Cette action est définitive : vos données personnelles (nom, email, téléphone, photo)
                    seront supprimées et vous serez déconnecté. Tapez <b>SUPPRIMER</b> pour confirmer.
                  </p>
                  <input value={deleteConfirm} onChange={(e) => setDeleteConfirm(e.target.value)}
                    placeholder="SUPPRIMER" data-testid="delete-account-confirm-input"
                    className="w-full bg-elevated rounded-xl px-3 py-2 text-sm outline-none border border-sand focus:border-brand-red" />
                  <div className="flex gap-2">
                    <button onClick={deleteAccount} disabled={deleteConfirm !== "SUPPRIMER" || deleteBusy}
                      data-testid="delete-account-confirm-btn"
                      className="flex-1 rounded-full bg-brand-red text-white py-2 text-sm font-semibold disabled:opacity-40">
                      {deleteBusy ? t("pr.deleting") : t("pr.confirmDelete")}
                    </button>
                    <button onClick={() => { setDeleteMode(false); setDeleteConfirm(""); }} data-testid="delete-account-cancel"
                      className="rounded-full border border-sand px-4 py-2 text-sm">{t("common.cancel")}</button>
                  </div>
                </div>
              )}
            </div>
          )}
        </aside>

        <div className="md:col-span-2" id="profile-orders">
          <h1 className="font-display text-3xl font-semibold mb-4 flex items-center gap-2"><Package className="w-6 h-6 text-brand-red" /> {t("profile.myOrders")}</h1>
          {orders.length === 0 ? (
            <div className="bg-white border border-sand rounded-2xl p-10 text-center text-mute">
              Aucune commande pour le moment.<br/>
              <Link to="/restaurants" className="text-brand-red font-semibold">{t("pr.discover")} →</Link>
            </div>
          ) : (
            <>
              <div className="space-y-3">
                {orders.map((o) => (
                  <Link to={`/orders/${o.order_id}`} key={o.order_id} data-testid={`order-row-${o.order_id}`} className="block bg-white border border-sand rounded-2xl p-4 hover:shadow-md transition-shadow">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-display text-lg font-semibold">{o.restaurant_name}</div>
                        <div className="text-sm text-mute flex items-center gap-3 mt-1">
                          <Clock className="w-3 h-3" /> {new Date(o.created_at).toLocaleString("fr-FR")}
                          <MapPin className="w-3 h-3" /> {o.zone}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-display text-lg font-semibold">{formatGNF(o.total)}</div>
                        <div className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full mt-1" style={{ backgroundColor: STATUS_COLOR[o.status] + "22", color: STATUS_COLOR[o.status] }}>
                          {STATUS_LABEL[o.status]}
                        </div>
                      </div>
                      <ChevronRight className="w-4 h-4 text-mute ml-2" />
                    </div>
                  </Link>
                ))}
              </div>
              {orders.length < ordersTotal && (
                <div className="text-center mt-4">
                  <button
                    onClick={() => fetchPage(orders.length, true)}
                    disabled={ordersLoading}
                    data-testid="load-more-orders"
                    className="rounded-full border border-sand bg-white hover:bg-elevated px-6 py-3 text-sm font-semibold disabled:opacity-60"
                  >
                    {ordersLoading ? t("common.loading") : t("pr.seeMore", { count: ordersTotal - orders.length })}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
